import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetExclusiveDealsApi } from './use-get-exclusive-deals-api';

const endpoint = 'ums/v1/cms/exclusive-deals';
export const updateProductOverrideEndpointIdentifier = endpoint;

interface BodyType {
  nameEn?: string;
  nameAr?: string;
  brandEn?: string;
  brandAr?: string;
  defaultUnit?: string;
  defaultPrice?: number;
  imageUrl?: string;
  specs?: string;
  origin?: string;
  state?: string;
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  dealId: number;
  body: BodyType;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useUpdateProductOverrideApi(options?: Options): ReturnType {
  const { refetch: refetchDeals } = useGetExclusiveDealsApi({ enabled: false });
  const toast = useCustomToast();

  const mutationFn = async ({ dealId, body }: TVariables) => {
    try {
      const { data } = await http.put(endpoint + `/${dealId}/product-override`, body);

      refetchDeals();

      toast({
        description: 'Product information has been updated successfully',
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { mutationKey: endpoint, ...options });
}
